import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import {
  MeterRating,
  MeterRatingIdType,
  MeterRatingSaveDto,
  meterRatingIdOptions,
} from 'src/app/models/meterRating.model';
import { PermissionsEnum } from 'src/app/models/role.model';
import { BPSAdminLevel } from 'src/app/models/user.model';
import { MeterRatingService } from 'src/app/services/meter-rating.service';
import { MeterService } from 'src/app/services/meter.service';
import { DtMeterTypeService } from 'src/app/services/dt-meter-type.service';
import { PermissionsService } from 'shared-lib';
import { TranslationUtils } from 'src/assets/i18n/translation.utils';

interface MultiSelectItem {
  id: number;
  name: string;
  checked: boolean;
}

@Component({
  selector: 'app-crud-meter-rating',
  templateUrl: './crud-meter-rating.component.html',
  styleUrls: ['./crud-meter-rating.component.scss'],
})
export class CrudMeterRatingComponent implements OnInit {
  public PermissionsEnum = PermissionsEnum;
  meterRatings: MeterRating[] = [];

  public currentPage: number = 1;
  public pageSize: number = 10;
  public count: number = 0;

  selectedMeterRating: MeterRating = new MeterRating();
  selectedRating: string = '';
  selectedUnit: string = '';
  selectedMeterRatingId: MeterRatingIdType = MeterRatingIdType.CustomerMeter;

  meterRatingIdOptions = meterRatingIdOptions;
  meterBrandItems: MultiSelectItem[] = [];
  dtMeterTypeItems: MultiSelectItem[] = [];

  meterBrandClicked: boolean = false;
  dtMeterTypeClicked: boolean = false;

  actionLabel$!: Observable<string>;
  isEditMode: boolean = false;

  @ViewChild('closeModal') closeModal: ElementRef;

  @Input() searchText!: string;
  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;

  constructor(
    private meterRatingService: MeterRatingService,
    public permissionsService: PermissionsService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    public meterService: MeterService,
    private dtMeterTypeService: DtMeterTypeService
  ) {}

  ngOnInit(): void {
    this.getMeterTypesForSelect();
    this.getDtMeterTypesForSelect();

    if (this.utilityId != 0) {
      this.reloadTable();
    }

    this.updateActionLabel();
  }

  private updateActionLabel(): void {
    const key = this.isEditMode ? TranslationUtils.EDIT_KEY : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  get showAssetMeterTypeField(): boolean {
    return this.selectedMeterRatingId === MeterRatingIdType.AssetMeter;
  }

  filter() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: this.searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllMeterRatings(dataIn);
  }

  reloadTable(searchText?: string, page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        SearchValue: searchText,
      },
      utilityId: this.utilityId,
    };

    this.getAllMeterRatings(dataIn);
  }

  addNew() {
    this.isEditMode = false;
    this.selectedMeterRating = new MeterRating();
    this.selectedRating = '';
    this.selectedUnit = '';
    this.selectedMeterRatingId = MeterRatingIdType.CustomerMeter;
    this.resetSelections();
    this.updateActionLabel();
  }

  selectMeterRating(meterRating: MeterRating) {
    this.isEditMode = true;
    this.selectedMeterRating = new MeterRating(meterRating);
    this.selectedRating = this.selectedMeterRating.rating;
    this.selectedUnit = this.selectedMeterRating.unit ?? '';
    this.selectedMeterRatingId =
      this.selectedMeterRating.meterRatingId ?? MeterRatingIdType.CustomerMeter;
    this.applySelectionsFrom(this.selectedMeterRating);
    this.updateActionLabel();
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable(this.searchText);
  }

  onMeterRatingIdChange() {
    if (!this.showAssetMeterTypeField) {
      this.dtMeterTypeItems.forEach((x) => (x.checked = false));
      this.dtMeterTypeClicked = !this.dtMeterTypeClicked;
    }
  }

  save() {
    const selectedBrandIds = this.meterBrandItems
      .filter((x) => x.checked)
      .map((x) => x.id);
    const selectedDtMeterTypeIds = this.dtMeterTypeItems
      .filter((x) => x.checked)
      .map((x) => x.id);

    if (selectedBrandIds.length === 0) {
      this.toastr.warning('Please select at least one Meter Type.');
      return;
    }

    if (
      this.selectedMeterRatingId === MeterRatingIdType.AssetMeter &&
      selectedDtMeterTypeIds.length === 0
    ) {
      this.toastr.warning('Please select at least one Asset Meter Type.');
      return;
    }

    const dataIn = new MeterRatingSaveDto({
      id: this.selectedMeterRating.id === 0 ? null : this.selectedMeterRating.id,
      rating: this.selectedRating,
      unit: this.selectedUnit,
      meterRatingId: this.selectedMeterRatingId,
      meterBrandIds: selectedBrandIds,
      dtMeterTypeIds: selectedDtMeterTypeIds,
    });

    this.meterRatingService.save(dataIn).subscribe({
      next: (response) => {
        if (response.status === 200 || response.status === 'OK') {
          this.closeModal?.nativeElement?.click();
          this.toastr.success(response.message);
          if (dataIn.id === null) {
            this.pageChange(1);
            return;
          }
          this.reloadTable(this.searchText);
        } else {
          this.toastr.error(response.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  customMessage(event: any) {
    event.target.setCustomValidity('Please enter a rating value.');
  }

  clearCustomMessage(event: any) {
    event.target.setCustomValidity('');
  }

  getMeterTypesForSelect() {
    this.meterService.getAllMeterTypesForSelect().subscribe({
      next: (response: any) => {
        var responseData = response?.body;
        if (responseData.status === 200 || responseData.status === 'OK') {
          this.meterBrandItems = (responseData?.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.name,
            checked: false,
          }));
        }
      },
      error: (_: any) =>
        this.toastr.error('An error ocurred while fetching meter types.'),
    });
  }

  getDtMeterTypesForSelect() {
    this.dtMeterTypeService.getAllForSelect().subscribe({
      next: (response: any) => {
        var responseData = response?.body;
        if (responseData.status === 200 || responseData.status === 'OK') {
          this.dtMeterTypeItems = (responseData?.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.type,
            checked: false,
          }));
        }
      },
      error: (_: any) =>
        this.toastr.error('An error ocurred while fetching Asset Meter Types.'),
    });
  }

  private resetSelections() {
    this.meterBrandItems.forEach((x) => (x.checked = false));
    this.dtMeterTypeItems.forEach((x) => (x.checked = false));
    this.meterBrandClicked = !this.meterBrandClicked;
    this.dtMeterTypeClicked = !this.dtMeterTypeClicked;
  }

  private applySelectionsFrom(meterRating: MeterRating) {
    this.meterBrandItems = this.meterBrandItems.map((x) => ({
      ...x,
      checked: meterRating.meterBrandIds.includes(x.id),
    }));
    this.dtMeterTypeItems = this.dtMeterTypeItems.map((x) => ({
      ...x,
      checked: meterRating.dtMeterTypeIds.includes(x.id),
    }));
    this.meterBrandClicked = !this.meterBrandClicked;
    this.dtMeterTypeClicked = !this.dtMeterTypeClicked;
  }

  private getAllMeterRatings(dataIn: any) {
    this.meterRatingService.getAll(dataIn).subscribe({
      next: (response) => {
        this.meterRatings = response.data.data.map(
          (meterRating: any) => new MeterRating(meterRating)
        );
        this.count = response.data.count;
        if (this.count == 0) {
          this.toastr.info('No data for given input.');
        }
      },
      error: (_) => this.toastr.error('An error ocurred.'),
    });
  }

  deleteMeterRating() {
    this.meterRatingService.delete(this.selectedMeterRating.id).subscribe({
      next: (response) => {
        this.closeModal?.nativeElement?.click();
        if (response.status === 200 || response.status === 'OK') {
          this.pageChange(1);
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  meterBrandNames(ids: number[]): string {
    return ids
      .map((id) => this.meterBrandItems.find((x) => x.id === id)?.name)
      .filter((name): name is string => !!name)
      .join(', ');
  }

  dtMeterTypeNames(ids: number[]): string {
    return ids
      .map((id) => this.dtMeterTypeItems.find((x) => x.id === id)?.name)
      .filter((name): name is string => !!name)
      .join(', ');
  }
}
